/**
 * Pantalla «Factures» d'ADMIN (`/admin/billing`, plans/facturacio-s1-lot-booking.md §4 i §7): client
 * de l'API, textos, lògica pura i hooks.
 *
 * Rutes (totes només ADMIN; el backend ho exigeix):
 * - `GET billing/status`: el mode del client d'emissió de Cuentica (per ensenyar-lo, no per emetre).
 * - `POST billing/booking-batches/preview`: el mes i l'extracte de Booking (text CSV) → documents. No desa res.
 * - `POST billing/booking-batches`: desa el lot en `DRAFT` (mateix cos). Si ja n'hi ha un de viu, 409.
 * - `GET billing/batches?period=` i `GET billing/batches/:id`: els lots d'un mes i el detall d'un.
 * - `POST billing/batches/:id/approve` i `.../discard`.
 * - `GET billing/reservations/:id`: l'estat de facturació d'una reserva (popover, tots els canals).
 * - `POST billing/documents/refresh` amb `{ reservationId }`: la posada al dia amb Cuentica (només lectures),
 *   en obrir el popover si l'estat és un esborrany o no se sap.
 *
 * Des d'aquí no s'emet res: l'emissió a Cuentica es fa des de VS Code (`scripts/dev/factures-booking.mjs`).
 * L'extracte es llegeix al navegador i s'envia com a text: el fitxer no es desa enlloc.
 */

import { useEffect, useRef } from 'react';
import { addMonths, format, isValid, parseISO, subMonths } from 'date-fns';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  BillingStatusDto,
  BookingBatchPreviewDto,
  BookingBatchPreviewRequest,
  CuenticaWriteMode,
  FiscalBlockerCode,
  FiscalDocumentDto,
  FiscalDocumentRefreshResultDto,
  FiscalDocumentStatus,
  FiscalLineKind,
  InvoiceBatchDetailDto,
  InvoiceBatchDto,
  InvoiceBatchStatus,
  ReservationBillingStatusDto,
} from '@guilleumes/types';
import { api } from '@/services/api';
import { langToLocale } from '@/lib/dates';
import { backendErrorMessage } from '@/lib/payment-charges';
import { formatCents } from '@/lib/payment-orders';

export { backendErrorMessage };

// ─── El que torna l'API i no és a `@guilleumes/types` ───────────────────────

/** Un document de la previsualització (sense id ni dades de Cuentica). */
export type PreviewDocument = BookingBatchPreviewDto['documents'][number];

/** Un document tal com el pinta la pantalla: el de la previsualització o el desat (amb el número de Cuentica). */
export type DocumentView = PreviewDocument &
  Partial<Pick<FiscalDocumentDto, 'id' | 'cuenticaNumber' | 'cuenticaSerie' | 'issuedAt' | 'lastError'>>;

/** L'empresa real de Cuentica (la de proves en té una altra). */
export const CUENTICA_REAL_COMPANY_ID = 22528;

/** El que es repeteix a la pantalla: on es fa l'emissió. */
export const ISSUE_FROM_VSCODE_TEXT =
  'L’emissió a Cuentica es fa des de VS Code (scripts/dev/factures-booking.mjs).';

export const APPROVE_CONFIRM_TEXT =
  'Aprovar vol dir que dones per bons aquests documents: a partir d’ara l’script de VS Code ' +
  '(scripts/dev/factures-booking.mjs) ja podrà emetre’ls a Cuentica, que els numerarà. Des d’aquí no ' +
  's’emet res, i els documents aturats no s’emetran fins que se’n resolgui el motiu.';

export const NO_STATEMENT_TEXT =
  'Sense extracte, la previsualització només surt del PMS: totes les reserves diran «No és a l’extracte».';

export const DISCARD_CONFIRM_TEXT =
  'Descartar deixa aquest lot fora: no se n’emetrà res i podràs desar-ne un de nou per a aquest mes. ' +
  'No toca Cuentica.';

// ─── Claus de React Query ────────────────────────────────────────────────────

export const billingKeys = {
  all: ['billing'] as const,
  status: ['billing', 'status'] as const,
  batchesAll: ['billing', 'batches'] as const,
  batches: (period: string) => ['billing', 'batches', { period }] as const,
  batchAll: ['billing', 'batch'] as const,
  batch: (id: string) => ['billing', 'batch', id] as const,
  reservationAll: ['billing', 'reservation'] as const,
  reservation: (id: string) => ['billing', 'reservation', id] as const,
};

// ─── Crides (fora dels hooks, per provar-les amb `api` mockejat) ─────────────

const enc = encodeURIComponent;

export async function fetchBillingStatus(): Promise<BillingStatusDto> {
  return (await api.get<BillingStatusDto>('/billing/status')).data;
}

export async function fetchInvoiceBatches(period: string): Promise<InvoiceBatchDto[]> {
  return (await api.get<InvoiceBatchDto[]>('/billing/batches', { params: { period } })).data;
}

export async function fetchInvoiceBatch(id: string): Promise<InvoiceBatchDetailDto> {
  return (await api.get<InvoiceBatchDetailDto>(`/billing/batches/${enc(id)}`)).data;
}

/** Sense extracte no s'envia el camp (el backend ho admet i avisa). */
export function bookingBatchBody(period: string, statementCsv: string | null): BookingBatchPreviewRequest {
  return statementCsv ? { period, statementCsv } : { period };
}

export async function previewBookingBatch(body: BookingBatchPreviewRequest): Promise<BookingBatchPreviewDto> {
  return (await api.post<BookingBatchPreviewDto>('/billing/booking-batches/preview', body)).data;
}

export async function createBookingBatch(body: BookingBatchPreviewRequest): Promise<InvoiceBatchDto> {
  return (await api.post<InvoiceBatchDto>('/billing/booking-batches', body)).data;
}

export async function approveInvoiceBatch(id: string): Promise<InvoiceBatchDto> {
  return (await api.post<InvoiceBatchDto>(`/billing/batches/${enc(id)}/approve`, {})).data;
}

export async function discardInvoiceBatch(id: string): Promise<InvoiceBatchDto> {
  return (await api.post<InvoiceBatchDto>(`/billing/batches/${enc(id)}/discard`, {})).data;
}

export async function fetchReservationBillingStatus(id: string): Promise<ReservationBillingStatusDto> {
  return (await api.get<ReservationBillingStatusDto>(`/billing/reservations/${enc(id)}`)).data;
}

/** Posada al dia dels documents d'una reserva (només lectures a Cuentica). */
export async function refreshReservationBillingDocuments(reservationId: string): Promise<FiscalDocumentRefreshResultDto> {
  return (await api.post<FiscalDocumentRefreshResultDto>('/billing/documents/refresh', { reservationId })).data;
}

// ─── Textos ──────────────────────────────────────────────────────────────────

const has = (object: object, key: string): boolean => Object.prototype.hasOwnProperty.call(object, key);
const label = <K extends string>(labels: Record<K, string>, key: string): string =>
  has(labels, key) ? labels[key as K] : key;

export const BATCH_STATUS_LABELS: Record<InvoiceBatchStatus, string> = {
  DRAFT: 'Esborrany',
  APPROVED: 'Aprovat',
  ISSUING: 'Emetent-se',
  ISSUED: 'Emès',
  PARTIAL: 'Emès en part',
  FAILED: 'Amb errors',
  DISCARDED: 'Descartat',
};
export const batchStatusLabel = (status: string): string => label(BATCH_STATUS_LABELS, status);

export const DOCUMENT_STATUS_LABELS: Record<FiscalDocumentStatus, string> = {
  PENDING: 'Per emetre',
  DRAFT: 'Esborrany a Cuentica',
  ISSUED: 'Emès',
  UNKNOWN: 'Sense resposta',
  FAILED: 'Fallit',
  BLOCKED: 'Aturat',
  EXCLUDED: 'Fora del lot',
};
export const documentStatusLabel = (status: string): string => label(DOCUMENT_STATUS_LABELS, status);

/** Títol curt de cada motiu; el detall ve del backend (`blocker.detail`). */
export const BLOCKER_LABELS: Record<FiscalBlockerCode, string> = {
  OVER_SIMPLIFIED_LIMIT: 'Més de 400 €: cal factura completa',
  AMOUNT_MISMATCH: 'L’import no quadra amb l’extracte',
  NOT_IN_PMS: 'No és al PMS',
  NOT_IN_STATEMENT: 'No és a l’extracte',
  CANCELLED_CHARGED: 'Cancel·lada i cobrada (ho decideix la gestoria)',
  ALREADY_INVOICED: 'Ja facturada',
  PRIOR_INCOME: 'Ja hi ha un ingrés a Cuentica',
  NOT_HOTEL_PROPERTY: 'Apartament (fora del lot)',
  CLOSED_PERIOD: 'Trimestre ja presentat',
};
export const blockerLabel = (code: string): string => label(BLOCKER_LABELS, code);

export const LINE_KIND_LABELS: Record<FiscalLineKind, string> = {
  STAY: 'Allotjament',
  TOURIST_TAX: 'Taxa turística',
  EXTRA: 'Extra',
  CANCELLATION_FEE: 'Cancel·lació',
  ADJUSTMENT: 'Ajust d’arrodoniment',
};
export const lineKindLabel = (kind: string): string => label(LINE_KIND_LABELS, kind);

export const MODE_LABELS: Record<CuenticaWriteMode, string> = {
  offline: 'fora de línia: no es crida Cuentica',
  readonly: 'lectura: consulta Cuentica i simula les escriptures',
  write: 'escriptura: escriu a Cuentica',
};
export const modeLabel = (mode: string): string => label(MODE_LABELS, mode);

/** Les línies de l'estat del client d'emissió, per a la franja fixa de dalt. */
export function billingStatusLines(status: BillingStatusDto): string[] {
  const lines = [`Mode: ${modeLabel(status.mode)}.`];
  lines.push(status.enabled ? 'Emissió activada en aquest servidor.' : 'Emissió apagada en aquest servidor.');
  if (status.companyId != null) {
    lines.push(
      status.companyId === CUENTICA_REAL_COMPANY_ID
        ? `Empresa de Cuentica: la real (${status.companyId}).`
        : `Empresa de Cuentica: ${status.companyId} (no és la real).`,
    );
  }
  lines.push(
    status.simplifiedSerie
      ? `Sèrie de simplificades: ${status.simplifiedSerie}.`
      : 'Falta la sèrie de simplificades (CUENTICA_SIMPLIFIED_SERIE): l’script no emetrà.',
  );
  return lines;
}

/** Punts bàsics → percentatge: 1000 → «10 %», 0 → «0 %», 550 → «5,5 %». */
export function vatRateText(bp: number): string {
  const pct = bp / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1).replace('.', ',')} %`;
}

/** «2026-10-31» → «31/10/2026». Si no és una data, tal com ve. */
export function operationDateText(day: string): string {
  const date = parseISO(day);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && isValid(date) ? format(date, 'dd/MM/yyyy') : day;
}

// ─── Lògica pura ─────────────────────────────────────────────────────────────

/** Només ADMIN (la ruta va dins de `RequireRole` i l'API també ho exigeix). */
export const canSeeBilling = (user: { role?: string | null } | null | undefined): boolean =>
  user?.role === 'ADMIN';

/** El mes per defecte: el passat (yyyy-MM, hora del navegador, que és la de l'hotel). */
export function defaultPeriod(now: Date = new Date()): string {
  return format(subMonths(now, 1), 'yyyy-MM');
}

export const isValidPeriod = (period: string): boolean =>
  /^\d{4}-(0[1-9]|1[0-2])$/.test(period);

/** «2026-10» → «octubre de 2026». */
export function periodLabel(period: string, lang: string = 'ca'): string {
  if (!isValidPeriod(period)) return period;
  const date = parseISO(`${period}-01`);
  return format(date, 'LLLL yyyy', { locale: langToLocale(lang) }).replace(/^(\S+) /, '$1 de ');
}

/** «2026-10» → «d’octubre de 2026»; «2026-11» → «de novembre de 2026» (apòstrof davant de vocal). */
export function ofPeriodLabel(period: string, lang: string = 'ca'): string {
  const text = periodLabel(period, lang);
  return /^[aeiouàèéíòóú]/i.test(text) ? `d’${text}` : `de ${text}`;
}

/** «2026-10» → «Octubre de 2026» (per al selector). */
export function periodOptionLabel(period: string, lang: string = 'ca'): string {
  const text = periodLabel(period, lang);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** El mes `delta` mesos després (o abans, si és negatiu). */
export function shiftPeriod(period: string, delta: number): string {
  if (!isValidPeriod(period)) return period;
  return format(addMonths(parseISO(`${period}-01`), delta), 'yyyy-MM');
}

/** Quants mesos enrere ofereix el selector (el d'ara inclòs). */
export const PERIOD_OPTIONS_COUNT = 24;

/**
 * Els mesos del selector, del més recent (el d'ara) al més antic. Mai un mes que encara no ha
 * començat: el lot és per data de sortida. Si `current` no hi és (un mes més antic), s'hi afegeix.
 */
export function periodOptions(now: Date = new Date(), current?: string, count = PERIOD_OPTIONS_COUNT): string[] {
  const options = Array.from({ length: count }, (_, i) => format(subMonths(now, i), 'yyyy-MM'));
  if (current && isValidPeriod(current) && !options.includes(current)) {
    options.push(current);
    options.sort((a, b) => b.localeCompare(a));
  }
  return options;
}

/** El lot viu del mes (el que no és descartat), si n'hi ha: només en pot haver un. */
export function liveBatch(batches: readonly InvoiceBatchDto[] | null | undefined): InvoiceBatchDto | null {
  return batches?.find((batch) => batch.status !== 'DISCARDED') ?? null;
}

/** Per què no es pot desar un lot nou, o `null` si es pot (només un lot viu per mes). */
export function saveBlockedReason(live: Pick<InvoiceBatchDto, 'status'> | null): string | null {
  if (!live) return null;
  return `Aquest mes ja té un lot (${batchStatusLabel(live.status).toLowerCase()}): descarta’l abans de desar-ne un de nou.`;
}

export const canApproveBatch = (batch: Pick<InvoiceBatchDto, 'status'>): boolean => batch.status === 'DRAFT';

/** El backend també ho rebutja si el lot té cap document `DRAFT`/`ISSUED`/`UNKNOWN`. */
export const canDiscardBatch = (batch: Pick<InvoiceBatchDto, 'status'>): boolean =>
  batch.status === 'DRAFT' || batch.status === 'APPROVED';

/** L'extracte diu un import diferent del del document. */
export const statementDiffers = (doc: Pick<PreviewDocument, 'statementAmountCents' | 'totalCents'>): boolean =>
  doc.statementAmountCents != null && doc.statementAmountCents !== doc.totalCents;

export type DocumentsFilter = 'all' | 'issuable' | 'blocked' | 'issued' | 'failed';

/** Un document que s'emetria (o que ja s'ha emès): sense motius i no exclòs ni aturat. */
export const isIssuableDocument = (doc: Pick<PreviewDocument, 'status' | 'blockers'>): boolean =>
  doc.blockers.length === 0 && doc.status !== 'BLOCKED' && doc.status !== 'EXCLUDED';

/** On és un document: el mateix criteri per a les rajoles, els imports i els filtres. */
export type DocumentGroup = 'pending' | 'issued' | 'failed' | 'blocked' | 'excluded';

/**
 * `PENDING`/`DRAFT` → per emetre; `ISSUED` → emès; `UNKNOWN`/`FAILED` → amb error; `BLOCKED` → aturat;
 * `EXCLUDED` → fora del lot. Un document per emetre que porti motius també compta com a aturat.
 */
export function documentGroup(doc: Pick<PreviewDocument, 'status' | 'blockers'>): DocumentGroup {
  if (doc.status === 'EXCLUDED') return 'excluded';
  if (doc.status === 'BLOCKED') return 'blocked';
  if (doc.status === 'ISSUED') return 'issued';
  if (doc.status === 'FAILED' || doc.status === 'UNKNOWN') return 'failed';
  return doc.blockers.length > 0 ? 'blocked' : 'pending';
}

/**
 * «Per emetre» i «Amb error» = el mateix conjunt que les rajoles; «Emesos» = ja amb número de Cuentica
 * (un lot emès en part en té de tots dos); «Aturats» = aturats i fora del lot. Tots junts són «Tots».
 */
export function filterDocuments<T extends Pick<PreviewDocument, 'status' | 'blockers'>>(
  docs: readonly T[],
  filter: DocumentsFilter,
): T[] {
  if (filter === 'issuable') return docs.filter((doc) => documentGroup(doc) === 'pending');
  if (filter === 'issued') return docs.filter((doc) => documentGroup(doc) === 'issued');
  if (filter === 'failed') return docs.filter((doc) => documentGroup(doc) === 'failed');
  if (filter === 'blocked') {
    return docs.filter((doc) => documentGroup(doc) === 'blocked' || documentGroup(doc) === 'excluded');
  }
  return [...docs];
}

// ─── Imports per grup (la pantalla els suma dels documents) ──────────────────

export interface AmountGroup {
  count: number;
  cents: number;
}

/**
 * Els documents d'un lot, per on són. La suma de `pending`, `issued`, `failed` i `blocked` és el total del
 * lot; `excluded` són els de fora (no s'emetran mai des d'aquest lot), que es compten a part perquè cada
 * targeta porta el seu import i un total de 0,00 € enganyaria.
 */
export interface DocumentAmounts {
  /** `PENDING` i `DRAFT` (esborrany a Cuentica): per emetre. */
  pending: AmountGroup;
  issued: AmountGroup;
  /** `UNKNOWN` i `FAILED`: la passada d'emissió no ha acabat bé. */
  failed: AmountGroup;
  blocked: AmountGroup;
  excluded: AmountGroup;
  /** Pending + issued + failed + blocked. */
  lot: AmountGroup;
  /** Els documents que surten a l'extracte: el que diu l'extracte i el que diu el PMS d'aquests mateixos. */
  statement: { count: number; statementCents: number; pmsCents: number };
}

export function documentAmounts(
  docs: ReadonlyArray<Pick<PreviewDocument, 'status' | 'blockers' | 'totalCents' | 'statementAmountCents'>>,
): DocumentAmounts {
  const group = (): AmountGroup => ({ count: 0, cents: 0 });
  const out: DocumentAmounts = {
    pending: group(),
    issued: group(),
    failed: group(),
    blocked: group(),
    excluded: group(),
    lot: group(),
    statement: { count: 0, statementCents: 0, pmsCents: 0 },
  };
  for (const doc of docs) {
    const target = out[documentGroup(doc)];
    target.count += 1;
    target.cents += doc.totalCents;
    if (doc.status !== 'EXCLUDED') {
      out.lot.count += 1;
      out.lot.cents += doc.totalCents;
    }
    if (doc.statementAmountCents != null) {
      out.statement.count += 1;
      out.statement.statementCents += doc.statementAmountCents;
      out.statement.pmsCents += doc.totalCents;
    }
  }
  return out;
}

/**
 * «Emesos 3 de 68 · 65 per emetre» quan la passada d'emissió ja ha tocat algun document (un lot emès en
 * part, amb `issue --limit`). `null` si encara no se n'ha emès cap ni n'ha fallat cap.
 */
export function issueProgressText(amounts: Pick<DocumentAmounts, 'pending' | 'issued' | 'failed'>): string | null {
  const { pending, issued, failed } = amounts;
  if (issued.count === 0 && failed.count === 0) return null;
  const parts = [`Emesos ${issued.count} de ${pending.count + issued.count + failed.count}`];
  if (failed.count > 0) parts.push(`${failed.count} amb error o sense resposta`);
  if (pending.count > 0) parts.push(`${pending.count} per emetre`);
  return parts.join(' · ');
}

/** Estats d'un lot en què la passada d'emissió ja ha començat (o ha pogut començar). */
const ISSUE_STARTED_STATUSES: ReadonlySet<string> = new Set(['ISSUING', 'PARTIAL', 'ISSUED', 'FAILED']);

/**
 * La llista n'ensenya el progrés (si n'hi ha: `issueProgressText` no diu res d'un lot sense cap emès ni
 * fallit). També `APPROVED`: una tanda que no n'emet cap deixa el lot en l'estat d'abans, però pot deixar
 * documents fallits o sense resposta.
 */
export const showsIssueProgress = (status: string): boolean =>
  status === 'APPROVED' || ISSUE_STARTED_STATUSES.has(status);

/**
 * La línia de recomptes d'un lot a la llista. Abans d'emetre, «per emetre»; un cop començada l'emissió,
 * els totals del backend compten també els ja emesos i es diu «emetibles» (el progrés va a sota).
 */
export function batchSummaryText(batch: Pick<InvoiceBatchDto, 'status' | 'totals'>): string {
  const { documents, issuable, issuableCents, blocked, excluded } = batch.totals;
  const issuableText = ISSUE_STARTED_STATUSES.has(batch.status) ? 'emetibles' : 'per emetre';
  const parts = [
    `${documents} documents`,
    `${issuable} ${issuableText} (${formatCents(issuableCents)})`,
    `${blocked} aturats`,
  ];
  if (excluded > 0) parts.push(`${excluded} fora del lot`);
  return parts.join(' · ');
}

/**
 * El número de Cuentica tal com el desa el backend (`245/2026/H`, que ja porta la sèrie). Si el número no
 * porta la sèrie al final, s'hi posa davant.
 */
export function cuenticaNumberText(doc: Pick<DocumentView, 'cuenticaNumber' | 'cuenticaSerie'>): string | null {
  if (!doc.cuenticaNumber) return null;
  const serie = doc.cuenticaSerie?.trim();
  if (!serie || doc.cuenticaNumber.endsWith(`/${serie}`)) return doc.cuenticaNumber;
  return `${serie} ${doc.cuenticaNumber}`;
}

/** «2026-11-03T10:15:00.000Z» → «03/11/2026» (hora del navegador). `null` si no és una data. */
export function issuedAtText(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = parseISO(iso);
  return isValid(date) ? format(date, 'dd/MM/yyyy') : null;
}

/** Els aturats primer (són els que cal mirar), després per data d'operació i referència. */
export function sortDocuments<T extends Pick<PreviewDocument, 'status' | 'blockers' | 'operationDate' | 'visibleRef'>>(
  docs: readonly T[],
): T[] {
  return [...docs].sort((a, b) => {
    const blocked = Number(isIssuableDocument(a)) - Number(isIssuableDocument(b));
    if (blocked !== 0) return blocked;
    return a.operationDate.localeCompare(b.operationDate) || a.visibleRef.localeCompare(b.visibleRef);
  });
}

/** L'extracte pot ser gran, però no tant: per sobre d'això, segur que no és el CSV de Booking. */
export const MAX_STATEMENT_BYTES = 5 * 1024 * 1024;

export function statementFileProblem(file: Pick<File, 'name' | 'size'>): string | null {
  if (file.size === 0) return 'El fitxer és buit.';
  if (file.size > MAX_STATEMENT_BYTES) return 'El fitxer passa de 5 MB: no sembla l’extracte de Booking.';
  if (!/\.(csv|txt)$/i.test(file.name)) return 'Ha de ser el CSV de l’extracte de pagaments de Booking.';
  return null;
}

// ─── Després d'escriure ──────────────────────────────────────────────────────

/** Desar, aprovar o descartar canvia la llista de lots del mes, el detall i l'estat de les reserves. */
export function invalidateAfterBatchWrite(queryClient: Pick<QueryClient, 'invalidateQueries'>): void {
  void queryClient.invalidateQueries({ queryKey: billingKeys.batchesAll });
  void queryClient.invalidateQueries({ queryKey: billingKeys.batchAll });
  void queryClient.invalidateQueries({ queryKey: billingKeys.reservationAll });
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useBillingStatus() {
  return useQuery({ queryKey: billingKeys.status, queryFn: fetchBillingStatus, staleTime: 60_000 });
}

export function useInvoiceBatches(period: string) {
  return useQuery({
    queryKey: billingKeys.batches(period),
    queryFn: () => fetchInvoiceBatches(period),
    enabled: isValidPeriod(period),
    staleTime: 30_000,
  });
}

export function useInvoiceBatch(id: string | null) {
  return useQuery({
    queryKey: billingKeys.batch(id ?? ''),
    queryFn: () => fetchInvoiceBatch(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Previsualitzar no desa res, però és un POST que surt d'un botó: va com a mutació. */
export function usePreviewBookingBatch() {
  return useMutation({ mutationFn: previewBookingBatch });
}

export function useCreateBookingBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBookingBatch,
    onSuccess: (batch: InvoiceBatchDto) => {
      invalidateAfterBatchWrite(queryClient);
      toast.success(`Lot desat com a esborrany: ${batch.totals.documents} documents.`);
    },
    onError: (error: unknown) => {
      toast.error(backendErrorMessage(error));
    },
  });
}

export function useApproveInvoiceBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: approveInvoiceBatch,
    onSuccess: () => {
      invalidateAfterBatchWrite(queryClient);
      toast.success('Lot aprovat. Ja es pot emetre des de VS Code.');
    },
    onError: (error: unknown) => {
      toast.error(backendErrorMessage(error));
    },
  });
}

export function useDiscardInvoiceBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: discardInvoiceBatch,
    onSuccess: () => {
      invalidateAfterBatchWrite(queryClient);
      toast.success('Lot descartat.');
    },
    onError: (error: unknown) => {
      toast.error(backendErrorMessage(error));
    },
  });
}

/** La reserva és de Booking (el canal tal com ve: «Booking.com», «booking», «A-Booking»…). */
export function isBookingChannel(channel: string | null | undefined): boolean {
  const key = (channel ?? '').trim().toLowerCase().replace(/^a-/, '').replace(/\.com$/, '').replace(/[^a-z0-9]/g, '');
  return key === 'booking';
}

/** Només quan `enabled` (el popover obert, ADMIN): cap petició si no. */
export function useReservationBillingStatus(reservationId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: billingKeys.reservation(reservationId ?? ''),
    queryFn: () => fetchReservationBillingStatus(reservationId as string),
    enabled: enabled && !!reservationId,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Estats d'un document viu: els mateixos que `LIVE_DOCUMENT_STATUSES` del backend
 * (`apps/backend/src/modules/billing/billing.constants.ts`). `EXCLUDED` és un document retirat (fora del
 * lot, o una directa desfeta), que `GET /billing/reservations/:id` també torna.
 */
export const LIVE_DOCUMENT_STATUSES: readonly FiscalDocumentStatus[] = [
  'PENDING',
  'DRAFT',
  'ISSUED',
  'UNKNOWN',
  'FAILED',
  'BLOCKED',
];
const isLiveDocument = (doc: { status: FiscalDocumentStatus }): boolean => LIVE_DOCUMENT_STATUSES.includes(doc.status);

/**
 * Cal posar al dia l'estat abans de fiar-se'n: algun document viu és un esborrany (la Carme el pot haver
 * emès a la web), està `UNKNOWN` o consta emès sense número. Els mateixos candidats que la posada al dia;
 * un retirat no ho és mai.
 */
export function needsBillingRefresh(status: ReservationBillingStatusDto | null | undefined): boolean {
  return (status?.documents ?? [])
    .filter(isLiveDocument)
    .some(
      (doc) => doc.status === 'DRAFT' || doc.status === 'UNKNOWN' || (doc.status === 'ISSUED' && !doc.cuenticaNumber),
    );
}

/**
 * Posada al dia en obrir el popover. Després, l'estat de la reserva es torna a llegir; si alguna cosa ha
 * canviat (emesa o esborrada a la web), també el calendari, perquè la card digui «Facturada». Un error
 * no surt com a toast: és una lectura de fons i el popover ensenya l'estat que ja tenia.
 */
export function useRefreshReservationBilling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshReservationBillingDocuments,
    onSuccess: (result: FiscalDocumentRefreshResultDto, reservationId: string) => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.reservation(reservationId) });
      if (result.issued > 0 || result.deleted > 0) {
        void queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
      }
    },
  });
}

/**
 * La posada al dia del popover, una sola vegada per obertura (un muntatge del cos) i reserva: quan
 * `needed` passa a cert, crida `refresh(reservationId)`; si després torna a ser cert (l'estat rellegit
 * encara és un esborrany) o el component es torna a pintar, no hi torna. Una altra reserva al mateix
 * cos sí que es posa al dia. Viu a part del popover perquè es pugui provar amb un render de debò.
 */
export function useRefreshBillingOncePerOpen(
  reservationId: string,
  needed: boolean,
  refresh: (reservationId: string) => void,
): void {
  const refreshedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!needed || refreshedForRef.current === reservationId) return;
    refreshedForRef.current = reservationId;
    refresh(reservationId);
  }, [reservationId, needed, refresh]);
}

/** `303/2026/H` (com el desa el PMS) → «H 303». Un format que no es reconeix surt tal qual. */
export function shortCuenticaNumber(number: string | null | undefined): string | null {
  const text = (number ?? '').trim();
  if (!text) return null;
  const m = /^(\d+)\/\d{4}\/(.+)$/.exec(text);
  return m ? `${m[2].trim()} ${m[1]}` : text;
}

/** El text curt de la insígnia del popover (sense dades personals). `null` = no se'n diu res. */
export function reservationBillingBadge(
  status: ReservationBillingStatusDto | null | undefined,
): { text: string; tone: 'ok' | 'pending' | 'warn' | 'none' } | null {
  if (!status) return null;
  const docs = status.documents;
  if (docs.length === 0) return { text: 'Sense factura', tone: 'none' };
  const issued = docs.filter((doc) => doc.status === 'ISSUED');
  if (issued.length > 0) {
    const numbers = [...new Set(issued.map((doc) => shortCuenticaNumber(doc.cuenticaNumber)).filter(Boolean))];
    return { text: numbers.length > 0 ? `Facturada · ${numbers.join(', ')}` : 'Facturada', tone: 'ok' };
  }
  // El document que mana és el primer viu: un esborrany abans que la resta. L'ordre del backend
  // (`operationDate`) no desempata un retirat i un de nou del mateix dia.
  const live = docs.filter(isLiveDocument);
  const doc = live.find((d) => d.status === 'DRAFT') ?? live[0];
  if (!doc) {
    // Només n'hi ha de retirats: una directa desfeta no té lot; un document de lot, fora del lot.
    const retired = docs[0];
    if (retired.kind === 'DIRECT') return { text: 'Esborrany desfet', tone: 'none' };
    const retiredWhere = retired.batchPeriod ? ` · lot ${ofPeriodLabel(retired.batchPeriod)}` : '';
    return { text: `Fora del lot${retiredWhere}`, tone: 'none' };
  }
  const where = doc.batchPeriod ? ` · lot ${ofPeriodLabel(doc.batchPeriod)}` : '';
  if (doc.status === 'DRAFT') return { text: `Esborrany de factura${where}`, tone: 'pending' };
  if (doc.status === 'BLOCKED' || doc.status === 'FAILED' || doc.status === 'UNKNOWN') {
    return { text: `${documentStatusLabel(doc.status)}${where}`, tone: 'warn' };
  }
  return { text: `${documentStatusLabel(doc.status)}${where}`, tone: 'pending' };
}
